import React from 'react';
import { rhythmFigureSprite } from '../../lib/rhythm/rhythmFigureSprites';
import type { RhythmFigureId } from '../../lib/rhythm/rhythmTokens';

interface RhythmFigureIconProps {
  figureId: RhythmFigureId;
  size?: number;
  color?: string;
  className?: string;
  /** Sin caja de color — útil dentro de ventanas del tren. */
  bare?: boolean;
}

const NOTE = '#2A2540';
const REST = '#6B6385';

/** Icono de figura rítmica: PNG ilustrado si existe, SVG para silencios. */
export function RhythmFigureIcon({
  figureId,
  size = 56,
  color,
  className,
  bare = false,
}: RhythmFigureIconProps) {
  const sprite = rhythmFigureSprite(figureId);

  if (sprite) {
    const w = size * sprite.aspectRatio;
    return (
      <img
        src={sprite.src}
        alt=""
        aria-hidden
        draggable={false}
        className={`rhythm-figure-img ${bare ? 'rhythm-figure-img--bare' : ''} ${className ?? ''}`.trim()}
        style={{ width: w, height: size }}
      />
    );
  }

  const fill = color ?? (figureId.startsWith('silencio') ? REST : NOTE);
  const h = size;
  const w = size * 1.1;

  const Head = ({ cx, cy, hollow = false }: { cx: number; cy: number; hollow?: boolean }) => (
    <ellipse
      cx={cx}
      cy={cy}
      rx={5.5}
      ry={4}
      fill={hollow ? 'none' : fill}
      stroke={fill}
      strokeWidth={hollow ? 1.4 : 0}
    />
  );

  const Stem = ({ x, y1, y2 }: { x: number; y1: number; y2: number }) => (
    <line x1={x} y1={y1} x2={x} y2={y2} stroke={fill} strokeWidth={1.6} strokeLinecap="round" />
  );

  const Beam = ({ x1, x2, y }: { x1: number; x2: number; y: number }) => (
    <rect x={x1} y={y} width={x2 - x1} height={3.2} rx={0.8} fill={fill} />
  );

  const RestQuarter = () => (
    <path
      d="M18 14c2 0 3.5 1.5 3.5 3.2 0 2.2-2.8 3.5-2.8 6.2 0 1.2 0.8 2.2 2 2.2"
      fill="none"
      stroke={fill}
      strokeWidth={2}
      strokeLinecap="round"
    />
  );

  const RestEighth = () => (
    <path
      d="M16 12c3 1 5 3 5 5.5 0 2-1.2 3.5-3 4.5M21 10v14"
      fill="none"
      stroke={fill}
      strokeWidth={2}
      strokeLinecap="round"
    />
  );

  const RestHalf = () => (
    <>
      <rect x={12} y={22} width={14} height={4} rx={1} fill={fill} />
      <rect x={12} y={26} width={14} height={4} rx={1} fill={fill} transform="translate(0 -6)" />
    </>
  );

  let content: React.ReactNode;

  switch (figureId) {
    case 'negra':
      content = (
        <>
          <Head cx={22} cy={30} />
          <Stem x={27.5} y1={12} y2={30} />
        </>
      );
      break;
    case 'blanca':
      content = (
        <>
          <Head cx={22} cy={30} hollow />
          <Stem x={27.5} y1={12} y2={30} />
        </>
      );
      break;
    case 'corchea_doble':
      content = (
        <>
          <Head cx={14} cy={30} />
          <Stem x={19.5} y1={12} y2={30} />
          <Head cx={30} cy={30} />
          <Stem x={35.5} y1={12} y2={30} />
          <Beam x1={19.5} x2={35.5} y={12} />
        </>
      );
      break;
    case 'cuartina':
      content = (
        <>
          {[10, 20, 30, 40].map((cx, i) => (
            <React.Fragment key={i}>
              <Head cx={cx} cy={30} />
              <Stem x={cx + 5.5} y1={12} y2={30} />
            </React.Fragment>
          ))}
          <Beam x1={15.5} x2={45.5} y={12} />
          <Beam x1={15.5} x2={45.5} y={16.2} />
        </>
      );
      break;
    case 'silencio_negra':
      content = <RestQuarter />;
      break;
    case 'silencio_corchea':
      content = <RestEighth />;
      break;
    case 'silencio_blanca':
      content = <RestHalf />;
      break;
    default:
      content = null;
  }

  return (
    <svg
      className={className}
      width={w}
      height={h}
      viewBox="0 0 52 44"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      {content}
    </svg>
  );
}

export default RhythmFigureIcon;
